import { useMutation, useReactiveVar } from "@apollo/client";
import { useEffect, useState } from "react";
import {
  GetOrCreateChatSessionMutation,
  GetOrCreateChatSessionMutationVariables,
} from "types/graphql-schema";

import AskAI, { ChatSessionContext } from "@apps/ask-ai/ask-ai";
import getOrCreateChatSessionMutation from "@apps/ask-ai/graphql/get-or-create-chat-session-mutation";
import Loading from "@components/loading/loading";

import { assessmentContextSidebarVar } from "./context-sidebar/context-sidebar";

const AssessmentAskAI = ({
  assessmentId,
  chatSessionId,
  startedChatSession,
  onChangeChatSessionId,
  onStartChatSession,
}: {
  assessmentId?: number;
  startedChatSession: boolean;
  chatSessionId: number | null;
  onChangeChatSessionId: (chatSessionId: number) => void;
  onStartChatSession: () => void;
}) => {
  const assessmentContextSidebar = useReactiveVar(assessmentContextSidebarVar);
  const questionId = assessmentContextSidebar?.questionId;

  const [hasPrompted, setHasPrompted] = useState(false);

  const [createChatSession, { loading: loadingCreateSession }] = useMutation<
    GetOrCreateChatSessionMutation,
    GetOrCreateChatSessionMutationVariables
  >(getOrCreateChatSessionMutation);

  const handleCreateAskAIChatSession = (assessmentQuestionId: number) => {
    if (loadingCreateSession) return;
    onStartChatSession();
    createChatSession({
      variables: {
        assessmentId,
        assessmentQuestionId,
        seedWithSuggestedAssessmentAnswer: true,
      },
      onCompleted: (response) => {
        const newChatSession = response?.getOrCreateChatbotSession?.chatSession;
        if (newChatSession) {
          onChangeChatSessionId(newChatSession.id);
        }
      },
    });
  };

  // We'll need to rework this once we have a way to create or retrieve a chat session for an assessment question.
  //
  useEffect(() => {
    if (waffle.flag_is_active("review-context-sidebar")) {
      return;
    }
    if (!waffle.flag_is_active("generated-context-in-ask-ai")) {
      return;
    }
    if (questionId && !hasPrompted) {
      setHasPrompted(true);
    }
    if (questionId) {
      handleCreateAskAIChatSession(questionId);
    }
  }, [questionId]);

  return loadingCreateSession ? (
    <Loading className="p-6">Asking AI for suggested responses...</Loading>
  ) : (
    <ChatSessionContext.Provider
      value={{ assessmentId, assessmentQuestionId: questionId }}
    >
      <AskAI
        chatSessionId={chatSessionId}
        onChangeChatSessionId={onChangeChatSessionId}
        onStartChatSession={onStartChatSession}
        isFullScreen={!!chatSessionId || startedChatSession}
        className="pt-6 px-6 pb-6"
        totalYPaddingAroundContainer={110}
      />
    </ChatSessionContext.Provider>
  );
};

export default AssessmentAskAI;
